import PropTypes from 'prop-types';
import React from 'react';
import { Statistics, Numbers, Tooltip, Loader } from '@weezevent/nacre';
import i18n from 'i18next';

import { useTranslation } from 'react-i18next';
import StatisticsTable from './components/statisticsTable';
import Styles from '../../../styles/event/dashboard/eventDashboard.module.css';
import StatisticsVariation from './components/statisticsVariation';
import { StatisticsMoreLabel } from './statisticsAttendees';

export default function StatisticsOrders({ data = null, currency = 'EUR', titleKey = 'total_orders', prefixUrl, eventStatus, eventEndDate }) {
    const { t } = useTranslation();
    const locale = i18n.language;

    const statistics = React.useMemo(() => {
        if (!data) {
            return null;
        }
        let tmpAttendeesStats = data.find(groupStats => groupStats.name === 'attendees_stats').value;
        let tmpOrdersStats = data.find(groupStats => groupStats.name === 'orders_stats').value;
        let tmpRevenueStats = data.find(groupStats => groupStats.name === 'revenues_stats').value;
        // Spreading order is important!
        return {
            ...tmpAttendeesStats,
            ...tmpRevenueStats,
            ...tmpOrdersStats,
            average_tickets_order: parseFloat(tmpOrdersStats.average_tickets_order).toFixed(1)
        };
    }, [data]);

    if (!statistics) {
        return (
            <Statistics stat="">
                <Loader inline="centered" />
            </Statistics>
        );
    }
    const rowsTableTop = {
        total_orders_web: {
            title: t('ticket.eventDashboard.statisticsOrders.totalOrdersWeb'),
            value: (
                <Statistics.CellStyle bold>
                    <Numbers value={statistics.total_orders_web} locale={locale} />
                </Statistics.CellStyle>
            )
        }
    };

    if (statistics.attendees_weez_web && statistics.total_orders_web) {
        rowsTableTop['average_tickets_order'] = {
            title: t('ticket.eventDashboard.statisticsOrders.averageTicketsOrder'),
            value: (
                <Statistics.CellStyle bold>
                    <Numbers value={parseInt(statistics.attendees_weez_web, 10) / parseInt(statistics.total_orders_web, 10)} locale={locale} />
                </Statistics.CellStyle>
            )
        };
    }

    rowsTableTop['average_cart_order_web'] = {
        title: (
            <Tooltip margin={false} position={'bottom'} triggerOnHover title={t('ticket.eventDashboard.statisticsOrders.averageCartOrderHelp')}>
                <span> {t('ticket.eventDashboard.statisticsOrders.averageCartOrder')} </span>
            </Tooltip>
        ),
        value: (
            <Statistics.CellStyle bold>
                <Numbers value={statistics.average_cart_order_web} locale={locale} currency={currency} showNullDecimal />
            </Statistics.CellStyle>
        )
    };

    if (statistics.average_cart_order_web && statistics.attendees_weez_web && statistics.total_orders_web) {
        rowsTableTop['average_price_attendee'] = {
            title: <span>{t('ticket.eventDashboard.statisticsOrders.averagePriceAttendee')}</span>,
            value: (
                <Statistics.CellStyle bold>
                    <Numbers
                        value={parseInt(statistics.average_cart_order_web, 10) / (parseInt(statistics.attendees_weez_web, 10) / parseInt(statistics.total_orders_web, 10))}
                        locale={locale}
                        currency={currency}
                        showNullDecimal
                    />
                </Statistics.CellStyle>
            )
        };
    }

    rowsTableTop['total_orders_offline'] = {
        title: (
            <Tooltip margin={false} position={'bottom'} triggerOnHover title={t('ticket.eventDashboard.statisticsOrders.offlineOrdersHelp')}>
                <span> {t('ticket.eventDashboard.statisticsOrders.offlineOrders')} </span>
            </Tooltip>
        ),
        value: <Statistics.CellStyle bold>{statistics.total_orders_offline}</Statistics.CellStyle>
    };

    const rowsTableBottom = {
        orders_in_progress: {
            title: <Statistics.CellStyle grey>{t('ticket.eventDashboard.statisticsOrders.ordersInProgress')}</Statistics.CellStyle>,
            value: (
                <Statistics.CellStyle bold grey>
                    {`+ `}
                    <Numbers value={statistics.orders_in_progress} locale={locale} />
                </Statistics.CellStyle>
            )
        }
    };

    return (
        <Statistics
            subtitle={t('ticket.eventDashboard.statisticsOrders.subtitle', { count: +statistics[titleKey] })}
            className={Styles['statistics-event-dashboard-card']}
            stat={<Numbers value={statistics[titleKey]} locale={locale} />}
            moreURL={`${prefixUrl}/manager/orders`}
            moreLabel={+statistics[titleKey] > 0 ? <StatisticsMoreLabel title={t('ticket.eventDashboard.statisticsOrders.link')} /> : null}
            footer={
                statistics[titleKey] > 0 && (
                    <StatisticsVariation
                        total={statistics[titleKey]}
                        variationUp={statistics.variation_last_week_up}
                        variationUnit={'%'}
                        variationLabel={t('ticket.eventDashboard.statisticsOrders.variationLabel')}
                        eventStatus={eventStatus}
                        eventEndDate={eventEndDate}
                    />
                )
            }
        >
            {+statistics[titleKey] > 0 && (
                <Statistics.Content>
                    <StatisticsTable statistics={statistics} rows={rowsTableTop} />
                    <StatisticsTable statistics={statistics} rows={rowsTableBottom} className={Styles['statistics-table-bottom']} />
                </Statistics.Content>
            )}
        </Statistics>
    );
}

StatisticsOrders.propTypes = {
    data: PropTypes.array,
    currency: PropTypes.string,
    titleKey: PropTypes.string,
    prefixUrl: PropTypes.string.isRequired,
    eventStatus: PropTypes.string.isRequired,
    eventEndDate: PropTypes.string
};
